import { differenceInHours, isFuture, isToday } from 'date-fns';
import { sum } from 'lodash';
import { useMemo } from 'react';
import { AuthUserType } from '../auth/types';
import { useAuthContext } from '../auth/useAuthContext';
import { brandConfig } from '../config';
import { BrandConfig } from '../config/brandConfig';
import { useGetReservationRevenueQuery } from '../redux/rtkQuery/apiSlice';
import lookUpZip from '../utils/look-up-zip';
import { ReservationGeneral } from './ReservationGeneral';
import { RevenueGeneral } from './RevenueGeneral';
import { ROIGeneral, ROISubType, ROIType } from './ROIGeneral';

const _emptyReservationUIObject = {
  Display_Travel_Insurance_Pitch: false,
  Display_Extend_Stay_Button: false,
  Disable_Extend_Stay_Button: false,
  Display_Request_Late_Check_Out_Button: false,
  Disable_Request_Late_Check_Out_Button: false,
  Display_Payment_Info: false,
  Display_View_Invoice: false,
  Display_Update_Payment_Method: false,
  Add_Service: false,
  Edit_Flight_Info: false,
  Display_Add_Check_In_Request_Linked: false,
  Display_Add_Check_In_Request: false,
  Disable_Add_Check_In_Request: false,
  Display_Add_Guests: false,
  Allow_Update_Guest: false,
  Do_Not_Display_Support_Section: false,
  Display_Access_Codes: false,
  Display_Balance_Due_Alert: false,
  Display_Balance_Due_Warning: false,
  Display_Financials: false,
  Balance_Due_On_Checkout: false,
  Display_Pay_By_Bank_Banner: false,
  Display_Expired_Offer_Warning: false,
};

export const buildReservationUIObject = (
  user?: AuthUserType,
  reservation?: ReservationGeneral,
  pendingPaymentAmount?: number,
  brand?: BrandConfig,
  rois?: ROIGeneral[],
  revenueData?: RevenueGeneral[]
) => {
  if (!reservation || typeof pendingPaymentAmount !== 'number') {
    return _emptyReservationUIObject;
  }
  const {
    extend_stay_allowed,
    external_revenue,
    has_late_check_out,
    has_travel_insurance,
    is_current_booking,
    is_future_booking,
    is_host,
    is_hybrid_child,
    is_hybrid_parent,
    is_inherited,
    is_past_booking,
    is_timeshare,
    late_check_out_allowed,
    late_check_out_amount,
    travel_insurance_eligible,
    is_balance_past_due,
    balance_due,
    hide_financials,
    check_out_ISO,
    is_guest_of_record,
    travel_insurance_deadline_ISO,
  } = reservation;

  const guestAssuranceROI = rois?.find(
    (roi) => roi.type === ROIType.MISC && roi.sub_type === ROISubType.GUEST_ASSURANCE_FEE
  );
  const hasGuestAssuranceROI = guestAssuranceROI && guestAssuranceROI?.total_amount > 0;

  const cardAuthorizationRevenue = revenueData?.find(
    (revenue) =>
      revenue.type === 'Authorization' &&
      ['Credit Card', 'Debit Card'].includes(revenue.tender) &&
      revenue.status !== 'Canceled'
  );
  const hasCardAuthorizationRevenue = cardAuthorizationRevenue !== undefined;

  const isVRBOBooking = reservation.booking_channel === 'VRBO';

  const balance_due_on_checkout = isToday(check_out_ISO);
  //--------- Display_Travel_Insurance_Pitch ---------//
  let Display_Travel_Insurance_Pitch =
    brandConfig.travelInsuranceEnabled &&
    is_host &&
    !is_hybrid_child &&
    !has_travel_insurance &&
    travel_insurance_eligible &&
    travel_insurance_deadline_ISO &&
    isFuture(travel_insurance_deadline_ISO) &&
    !is_inherited;

  if (Display_Travel_Insurance_Pitch) {
    // We only want to do this lookup if Display_Travel_Insurance_Pitch passes
    // the initial checks. If it doesn't, we don't want to do the lookup.
    // This is to avoid unnecessary API calls.
    let isInsurableLocation = false;

    if (user?.country && ['US', 'CA'].includes(user.country)) {
      if (user.state && ['NY', 'New York'].includes(user.state)) {
        isInsurableLocation = false;
      } else {
        isInsurableLocation = true;
      }
    }

    if (!user?.country && !user?.state && user?.zipCode) {
      lookUpZip(user.zipCode)
        .then((location) => {
          const { address_components } = location[0];
          const { short_name: StateCode } = address_components.find(
            (component: { types: string[] }) =>
              component.types.includes('administrative_area_level_1')
          );
          const { short_name: CountryCode } = address_components.find(
            (component: { types: string[] }) => component.types.includes('country')
          );
          isInsurableLocation = StateCode !== 'NY' && ['US', 'CA'].includes(CountryCode); // We can only insure 49 states and Canada (NY is not insurable)
          return isInsurableLocation;
        })
        .catch(() => {
          isInsurableLocation = false;
        }); // If the API fails, we assume the location is outside of North America (US & Canada) and is uninsurable
    }
    Display_Travel_Insurance_Pitch = isInsurableLocation;
  }
  //-------------------------------------//

  return {
    Display_Travel_Insurance_Pitch,
    Display_Extend_Stay_Button: is_host && !is_past_booking && !is_hybrid_parent && !is_timeshare,
    Disable_Extend_Stay_Button: !extend_stay_allowed,
    Display_Request_Late_Check_Out_Button:
      is_host && // must be host
      !is_past_booking && // must not be past booking
      !is_timeshare && // must not be timeshare
      !is_hybrid_parent && // must not be hybrid parent
      !!late_check_out_amount && // must have late check out amount
      differenceInHours(check_out_ISO, new Date()) < 48, // must be less than 48 hours from checkout
    Disable_Request_Late_Check_Out_Button: !late_check_out_allowed || has_late_check_out,
    Display_Payment_Info: is_host && !is_hybrid_child && !external_revenue,
    Display_View_Invoice: !external_revenue,
    Add_Service: is_host && !is_past_booking && !is_timeshare,
    Edit_Flight_Info: is_host,
    Display_Add_Check_In_Request_Linked:
      is_host &&
      is_future_booking &&
      !is_timeshare &&
      is_hybrid_parent &&
      brand?.allowCheckInRequests,
    Display_Add_Check_In_Request:
      is_host &&
      is_future_booking &&
      !is_timeshare &&
      !is_hybrid_parent &&
      brand?.allowCheckInRequests,
    Disable_Add_Check_In_Request: !brand?.allowCheckInRequests || false,
    Display_Add_Guests: is_host && !is_past_booking,
    Allow_Update_Guest: is_host && !is_past_booking,
    Do_Not_Display_Support_Section: is_past_booking,
    Display_Access_Codes:
      is_current_booking && !is_timeshare && !(!external_revenue && balance_due > 0),
    Display_Balance_Due_Alert:
      (is_balance_past_due &&
        is_host &&
        !external_revenue &&
        balance_due - pendingPaymentAmount > 0) ||
      (balance_due_on_checkout &&
        is_host &&
        !external_revenue &&
        balance_due - pendingPaymentAmount > 0),
    Display_Balance_Due_Warning:
      balance_due > 0 &&
      !is_balance_past_due &&
      is_host &&
      !external_revenue &&
      balance_due - pendingPaymentAmount > 0,
    Display_Financials: !hide_financials,
    Append_Pay_By_Bank: isVRBOBooking && hasGuestAssuranceROI,
    Display_Pay_By_Bank_Banner:
      isVRBOBooking && hasGuestAssuranceROI && hasCardAuthorizationRevenue,
    Display_Update_Payment_Method: is_guest_of_record,
    Display_Expired_Offer_Warning:
      isVRBOBooking && hasGuestAssuranceROI && !hasCardAuthorizationRevenue,
  };
};

export default function useReservationUIConfig(
  reservation?: ReservationGeneral,
  rois?: ROIGeneral[]
) {
  const { user } = useAuthContext();
  const {
    data: revenueData,
    isSuccess: revenueSuccess,
    isFetching: revenueFetching,
    // isError: revenueError,
    // error: revenueErrorObject
  } = useGetReservationRevenueQuery(
    {
      reservationName: reservation?.name,
    },
    { skip: !reservation || !reservation.is_host }
  );

  const pendingPayments = !revenueSuccess
    ? []
    : revenueData.filter((payment) => payment.status === 'Pending');
  const pendingPaymentAmount = sum(pendingPayments.map((payment) => payment.amount));

  const uiData = useMemo(
    () =>
      buildReservationUIObject(
        user,
        reservation,
        pendingPaymentAmount,
        brandConfig,
        rois,
        revenueData
      ),
    [reservation, pendingPaymentAmount, rois, revenueData, user]
  );
  if (revenueFetching) {
    return _emptyReservationUIObject;
  }
  return uiData;
}
